/**
 * Named column configurations persisted to localStorage, per table.
 *
 * A "column configuration" is purely about columns — which are visible, their
 * order, and the sort. It deliberately does NOT capture filters: that richer,
 * shareable concept is a database-backed "Saved View" (Search Companies /
 * Search Shipments), kept separate to avoid overloading one mechanism.
 */

export interface SavedColumnConfiguration {
  configId: string;
  configName: string;
  columnConfig: {
    hiddenKeys: string[];
    columnOrder: string[];
  };
  sortConfig?:
    | {
        sortKey: string;
        sortDir: "asc" | "desc";
      }
    | undefined;
  createdAt: string;
}

function storageKey(tableId: string): string {
  return `hubble-table-configs-${tableId}`;
}

export function loadColumnConfigurations(
  tableId: string,
): SavedColumnConfiguration[] {
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (!raw) return [];
    return JSON.parse(raw) as SavedColumnConfiguration[];
  } catch {
    return [];
  }
}

export function saveColumnConfiguration(
  tableId: string,
  config: SavedColumnConfiguration,
): SavedColumnConfiguration[] {
  const configs = loadColumnConfigurations(tableId);
  configs.push(config);
  localStorage.setItem(storageKey(tableId), JSON.stringify(configs));
  return configs;
}

export function updateColumnConfiguration(
  tableId: string,
  configId: string,
  update: Partial<Omit<SavedColumnConfiguration, "configId">>,
): SavedColumnConfiguration[] {
  const configs = loadColumnConfigurations(tableId);
  const index = configs.findIndex((c) => c.configId === configId);
  if (index >= 0) {
    configs[index] = { ...configs[index]!, ...update };
    localStorage.setItem(storageKey(tableId), JSON.stringify(configs));
  }
  return configs;
}

export function deleteColumnConfiguration(
  tableId: string,
  configId: string,
): SavedColumnConfiguration[] {
  const configs = loadColumnConfigurations(tableId).filter(
    (c) => c.configId !== configId,
  );
  localStorage.setItem(storageKey(tableId), JSON.stringify(configs));
  return configs;
}
